import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from "@angular/core";
import { Store } from "@ngrx/store";
import { NavigationEnd, Router } from "@angular/router";
import { Subject, filter, startWith, takeUntil } from "rxjs";
import {
  resetVoucher,
  selectVoucherPharmacy,
} from "../../store/voucher/voucher.actions";
import { GetSelectedVoucherPharmacy } from "../../store/voucher/voucher.selector";
import { VoucherPharmacyStorageService } from "../../services/voucher-pharmacy-storage.service";
import { NavigationService } from "../../services/navigation.service";
import { DashboardRoutesType } from "../dashboard/dashboard-routes.type";
import { BasePageAnimationDirection } from "../../animations/animation-base/animation.types";

/**
 * Rahmen des Einlöse-Vorgangs. Die vier Schritte sind eigene Routen und
 * liegen im Router-Outlet; der Zustand dazu steht im voucher-Slice.
 */
@Component({
  selector: "app-voucher-redeem",
  templateUrl: "./voucher-redeem.component.html",
  styleUrls: ["./voucher-redeem.component.scss"],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class VoucherRedeemComponent implements OnInit, OnDestroy {
  public readonly STEPS = [
    "PAGES.VOUCHER.STEPS.SCAN",
    "PAGES.VOUCHER.STEPS.AMOUNT",
    "PAGES.VOUCHER.STEPS.REDEEM",
  ];

  /**
   * ⚠ Der letzte Schritt liegt hinter dem letzten Index: auf der Erfolgsseite
   * ist auch "Prüfen & Einlösen" erledigt.
   */
  private static readonly STEP_BY_SEGMENT: Record<string, number> = {
    betrag: 1,
    reservierung: 2,
    erfolg: 3,
  };

  public step = 0;

  public showLeaveConfirm = false;

  private _destroy = new Subject<void>();

  constructor(
    private _store: Store,
    private _navigationService: NavigationService,
    private _router: Router,
    private _pharmacyStorage: VoucherPharmacyStorageService,
  ) {}

  ngOnInit(): void {
    // Der Rahmen bleibt über alle Schritte stehen — das läuft also einmal je
    // Besuch und nicht bei jedem Schrittwechsel.
    // ⚠ Vor dem Reset lesen: der Reset setzt die Auswahl auf null, und das
    // Abo darunter schriebe diese null sofort in den Speicher.
    const stored = this._pharmacyStorage.load();
    this._store.dispatch(resetVoucher());

    if (stored) {
      this._store.dispatch(selectVoucherPharmacy({ pharmacy: stored }));
    }

    this._store
      .select(GetSelectedVoucherPharmacy)
      .pipe(takeUntil(this._destroy))
      .subscribe((pharmacy) => this._pharmacyStorage.save(pharmacy));

    // ⚠ startWith, weil beim Betreten schon navigiert wurde: ohne das bliebe
    // der Stepper bis zum naechsten Wechsel auf Schritt 1 stehen.
    this._router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        startWith(null),
        takeUntil(this._destroy),
      )
      .subscribe(() => (this.step = this.stepFromUrl(this._router.url)));
  }

  /** Wer den Vorgang verlässt, startet beim nächsten Mal ohne Vorauswahl. */
  ngOnDestroy(): void {
    this._destroy.next();
    this._destroy.complete();
    this._pharmacyStorage.clear();
  }

  /** Der letzte Pfadabschnitt benennt den Schritt; alles Unbekannte ist der erste. */
  private stepFromUrl(url: string): number {
    const segment = url.split(/[?#]/)[0].split("/").filter(Boolean).pop() ?? "";

    return VoucherRedeemComponent.STEP_BY_SEGMENT[segment] ?? 0;
  }

  /**
   * Zurueck zur Startseite ueber den Link in der Leiste.
   * ⚠ Nur die mittleren Schritte fragen nach — im ersten ist noch nichts
   * eingegeben und auf der Erfolgsseite ist schon gebucht.
   */
  public goToDashboard(): void {
    if (this.step === 1 || this.step === 2) {
      this.showLeaveConfirm = true;

      return;
    }

    this.leave();
  }

  /** Der bestaetigte Abbruch; der Rahmen raeumt beim naechsten Betreten auf. */
  public leave(): void {
    this._navigationService.navigateByUrl(
      DashboardRoutesType.Dashboard,
      BasePageAnimationDirection.RIGHT,
    );
  }
}
